import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import {
	useLazyGetCompanyEnrichCompanyDetailQuery,
	usePreviewCompanySearchMutation,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";
import { classNames } from "@/utils/classNames";
import { Avatar } from "@/components/Avatar/Avatar";
import { Spinner } from "@/components/Spinner/Spinner";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import type { CompanySearchPreviewItem } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

export interface CompanyLinkedinSelection {
	name: string;
	domain?: string | null;
	logoUrl?: string | null;
	linkedinUrl: string;
}

interface CompanyLinkedinComboboxProps {
	value: CompanyLinkedinSelection | null;
	onSelect: (selection: CompanyLinkedinSelection) => void;
	onClear: () => void;
	/** Fired when the user can't find their company (or the pick has no LinkedIn page) — switch to manual URL entry. */
	onManualEntry: () => void;
	portalContainer?: HTMLElement | null;
}

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_RESULTS = 5;

/** A company row's display name, falling back to its domain when unnamed. */
const companyLabel = (company: { name?: string | null; domain?: string | null }): string =>
	company.name?.trim() || company.domain?.trim() || "";

/**
 * Typeahead for the user's company LinkedIn page (Profile & Company onboarding
 * step): searches companies by name via CompanyEnrich, then resolves the picked
 * company's LinkedIn URL from the company detail endpoint.
 */
export const CompanyLinkedinCombobox: FunctionComponent<CompanyLinkedinComboboxProps> = ({
	value,
	onSelect,
	onClear,
	onManualEntry,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.profileStep.${key}`);

	const [query, setQuery] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [results, setResults] = useState<CompanySearchPreviewItem[]>([]);
	const [resolvingId, setResolvingId] = useState<string | null>(null);
	const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
		null
	);

	const anchorRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, isOpen);

	const [fetchCompanies, { isLoading: isSearching }] = usePreviewCompanySearchMutation();
	const [fetchCompanyDetail] = useLazyGetCompanyEnrichCompanyDetailQuery();

	useEffect(() => {
		if (query.trim().length < MIN_QUERY_LENGTH) {
			setResults([]);
			setIsOpen(false);

			return;
		}

		const handle = setTimeout(async () => {
			try {
				const response = await fetchCompanies({ query: query.trim() }).unwrap();

				setResults(response.items.slice(0, MAX_RESULTS));
				setIsOpen(true);
			} catch (err) {
				console.error(err);
			}
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(handle);
	}, [query, fetchCompanies]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const updateRect = () => {
			const rect = anchorRef.current?.getBoundingClientRect();

			if (rect) {
				setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
			}
		};

		updateRect();
		window.addEventListener("scroll", updateRect, true);
		window.addEventListener("resize", updateRect);

		return () => {
			window.removeEventListener("scroll", updateRect, true);
			window.removeEventListener("resize", updateRect);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleOutsideClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (
				target instanceof Node &&
				!anchorRef.current?.contains(target) &&
				!menuRef.current?.contains(target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);

		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isOpen]);

	const handlePick = async (company: CompanySearchPreviewItem) => {
		setResolvingId(company.id);

		try {
			const detail = await fetchCompanyDetail(company.id).unwrap();

			if (!detail.linkedinUrl) {
				onManualEntry();

				return;
			}

			onSelect({
				name: companyLabel(detail) || companyLabel(company),
				domain: detail.domain ?? company.domain,
				logoUrl: detail.logoUrl ?? company.logo_url,
				linkedinUrl: detail.linkedinUrl,
			});
			setIsOpen(false);
			setQuery("");
		} catch (err) {
			console.error(err);
			onManualEntry();
		} finally {
			setResolvingId(null);
		}
	};

	if (value) {
		return (
			<div className="input-styles flex w-full items-center gap-2 rounded-lg border px-3 py-2">
				<Avatar firstName={value.name} imageSrc={value.logoUrl ?? undefined} lastName="" />
				<span className="min-w-0 flex-1 truncate">
					<span className="block truncate text-xs font-semibold">{value.name}</span>
					{value.domain && (
						<span className="block truncate text-[11px] text-text-meta">{value.domain}</span>
					)}
				</span>
				<button
					className="cursor-pointer p-1 text-text-meta hover:text-status-error"
					title={ts("clearSelection")}
					type="button"
					onClick={onClear}
				>
					✕
				</button>
			</div>
		);
	}

	const menu =
		isOpen && menuRect ? (
			<div
				ref={menuRef}
				className="fixed z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
				style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
			>
				{isSearching && results.length === 0 ? (
					<div className="flex items-center justify-center p-3">
						<Spinner color="#4D66F5" />
					</div>
				) : (
					results.map((company) => (
						<button
							key={company.id}
							className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
							type="button"
							onClick={() => handlePick(company)}
						>
							<Avatar
								firstName={companyLabel(company)}
								imageSrc={company.logo_url ?? undefined}
								lastName=""
							/>
							<span className="min-w-0 flex-1">
								<span className="block truncate text-xs font-semibold">
									{companyLabel(company)}
								</span>
								{company.domain && (
									<span className="block truncate text-[11px] text-text-meta">
										{company.domain}
									</span>
								)}
							</span>
							{resolvingId === company.id && <Spinner color="#4D66F5" />}
						</button>
					))
				)}
				<button
					className="w-full cursor-pointer border-t border-gray-200 px-3 py-2 text-left text-xs font-semibold text-brand-light hover:bg-gray-50"
					type="button"
					onClick={() => {
						setIsOpen(false);
						onManualEntry();
					}}
				>
					{ts("cantFindCompany")}
				</button>
			</div>
		) : null;

	return (
		<div ref={anchorRef} className="w-full">
			<input
				autoComplete="off"
				className={classNames("input-styles w-full rounded-lg border px-3 py-2 text-xs")}
				placeholder={ts("companyTypeaheadPlaceholder")}
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				onFocus={() => {
					if (results.length > 0 && query.trim().length >= MIN_QUERY_LENGTH) {
						setIsOpen(true);
					}
				}}
			/>
			{menu && ReactDOM.createPortal(menu, portalContainer ?? document.body)}
		</div>
	);
};
